import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
	activatePlan,
	createPlan,
	deletePlan,
	getPlanDetail,
	listPlans,
	renamePlan,
	setSlotZones,
	updateSlot,
	SorterPlanDetail,
	SorterPlanSummary,
	UpdateSorterPlanSlotRequest,
} from "../api/sorting-machine";
import { fetchZones } from "../lib/OptionListApi";
import buildZoneTree from "../pages/Arrangments/SortingMachineTab/buildZoneTree";
import { ZoneNode, ZoneRaw } from "../types/sortingMachine";
import useLang from "./useLang";

type SlotPatch = Partial<
	Pick<
		UpdateSorterPlanSlotRequest,
		"displayName" | "isErrorSlot" | "isEnabled"
	>
>;

interface LoadingState {
	plans: boolean;
	detail: boolean;
	mutating: boolean;
}

const initialLoading: LoadingState = {
	plans: false,
	detail: false,
	mutating: false,
};

export const useSortingMachine = () => {
	const { strings } = useLang("sortingMachineTab");

	const [plans, setPlans] = useState<SorterPlanSummary[]>([]);
	const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
	const [selectedPlanDetail, setSelectedPlanDetail] =
		useState<SorterPlanDetail | null>(null);
	const [flatZones, setFlatZones] = useState<ZoneRaw[]>([]);
	const [loading, setLoading] = useState<LoadingState>(initialLoading);

	const refreshPlans = useCallback(async (): Promise<SorterPlanSummary[]> => {
		setLoading((prev) => ({ ...prev, plans: true }));
		try {
			const next = await listPlans();
			setPlans(next);
			return next;
		} finally {
			setLoading((prev) => ({ ...prev, plans: false }));
		}
	}, []);

	const refreshPlanDetail = useCallback(
		async (planId: number): Promise<SorterPlanDetail | null> => {
			setLoading((prev) => ({ ...prev, detail: true }));
			try {
				const next = await getPlanDetail(planId);
				setSelectedPlanDetail(next);
				return next;
			} finally {
				setLoading((prev) => ({ ...prev, detail: false }));
			}
		},
		[]
	);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			const [planList, zones] = await Promise.all([
				listPlans().catch(() => [] as SorterPlanSummary[]),
				fetchZones().catch(() => undefined),
			]);
			if (cancelled) return;
			setPlans(planList);
			if (Array.isArray(zones)) {
				setFlatZones(zones as ZoneRaw[]);
			} else {
				toast.error(
					strings?.toasts?.zoneLoadError ?? "Failed to load zones"
				);
			}
			if (planList.length > 0) {
				const activeOrFirst =
					planList.find((p) => p.isActive) ?? planList[0];
				setSelectedPlanId(activeOrFirst.id);
			}
		})();
		return () => {
			cancelled = true;
		};
		// strings?.toasts?.zoneLoadError intentionally omitted — only the initial mount load should run here
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedPlanId === null) {
			setSelectedPlanDetail(null);
			return;
		}
		let cancelled = false;
		(async () => {
			setLoading((prev) => ({ ...prev, detail: true }));
			try {
				const detail = await getPlanDetail(selectedPlanId);
				if (!cancelled) setSelectedPlanDetail(detail);
			} catch {
				if (!cancelled) setSelectedPlanDetail(null);
			} finally {
				if (!cancelled)
					setLoading((prev) => ({ ...prev, detail: false }));
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [selectedPlanId]);

	const zoneTree: ZoneNode[] = useMemo(
		() => buildZoneTree(flatZones),
		[flatZones]
	);

	const activePlan = useMemo(
		() => plans.find((p) => p.isActive) ?? null,
		[plans]
	);

	const withMutation = useCallback(
		async <T>(fn: () => Promise<T>): Promise<T | undefined> => {
			setLoading((prev) => ({ ...prev, mutating: true }));
			try {
				return await fn();
			} catch {
				return undefined;
			} finally {
				setLoading((prev) => ({ ...prev, mutating: false }));
			}
		},
		[]
	);

	const handleSelectPlan = useCallback((planId: number) => {
		setSelectedPlanId(planId);
	}, []);

	const handleCreatePlan = useCallback(
		async (name: string) => {
			const created = await withMutation(() =>
				createPlan({ name: name.trim() })
			);
			if (!created) return;
			toast.success(strings?.toasts?.planCreated ?? "Plan created");
			await refreshPlans();
			setSelectedPlanId(created.id);
		},
		[refreshPlans, strings?.toasts?.planCreated, withMutation]
	);

	const handleRenamePlan = useCallback(
		async (planId: number, name: string) => {
			const ok = await withMutation(async () => {
				await renamePlan(planId, { name: name.trim() });
				return true;
			});
			if (!ok) return;
			toast.success(strings?.toasts?.planRenamed ?? "Plan renamed");
			await refreshPlans();
		},
		[refreshPlans, strings?.toasts?.planRenamed, withMutation]
	);

	const handleDeletePlan = useCallback(
		async (planId: number) => {
			const ok = await withMutation(async () => {
				await deletePlan(planId);
				return true;
			});
			if (!ok) return;
			toast.success(strings?.toasts?.planDeleted ?? "Plan deleted");
			const next = await refreshPlans();
			if (selectedPlanId === planId) {
				setSelectedPlanId(next[0]?.id ?? null);
			}
		},
		[
			refreshPlans,
			selectedPlanId,
			strings?.toasts?.planDeleted,
			withMutation,
		]
	);

	const handleActivatePlan = useCallback(
		async (planId: number) => {
			const ok = await withMutation(async () => {
				await activatePlan(planId);
				return true;
			});
			if (!ok) return;
			toast.success(
				strings?.toasts?.activePlanChanged ?? "Active plan changed"
			);
			await refreshPlans();
		},
		[refreshPlans, strings?.toasts?.activePlanChanged, withMutation]
	);

	const handleUpdateSlot = useCallback(
		async (planSlotId: number, patch: SlotPatch) => {
			if (!selectedPlanDetail) return;
			const current = selectedPlanDetail.slots.find(
				(s) => s.id === planSlotId
			);
			if (!current) return;

			const body: UpdateSorterPlanSlotRequest = {
				displayName: patch.displayName ?? current.displayName,
				isErrorSlot: patch.isErrorSlot ?? current.isErrorSlot,
				isEnabled: patch.isEnabled ?? current.isEnabled,
			};

			const ok = await withMutation(async () => {
				await updateSlot(selectedPlanDetail.id, planSlotId, body);
				return true;
			});
			if (!ok) return;
			toast.success(strings?.toasts?.slotUpdated ?? "Slot updated");
			await refreshPlanDetail(selectedPlanDetail.id);
		},
		[
			refreshPlanDetail,
			selectedPlanDetail,
			strings?.toasts?.slotUpdated,
			withMutation,
		]
	);

	const handleSetSlotZones = useCallback(
		async (planSlotId: number, zoneIds: number[]) => {
			if (!selectedPlanDetail) return;
			const ok = await withMutation(async () => {
				await setSlotZones(selectedPlanDetail.id, planSlotId, {
					zoneIds,
				});
				return true;
			});
			if (!ok) return;
			toast.success(
				strings?.toasts?.mappingSaved ?? "Zone mapping saved"
			);
			await refreshPlanDetail(selectedPlanDetail.id);
		},
		[
			refreshPlanDetail,
			selectedPlanDetail,
			strings?.toasts?.mappingSaved,
			withMutation,
		]
	);

	return {
		plans,
		activePlan,
		selectedPlanId,
		selectedPlanDetail,
		flatZones,
		zoneTree,
		loading,
		selectPlan: handleSelectPlan,
		createPlan: handleCreatePlan,
		renamePlan: handleRenamePlan,
		deletePlan: handleDeletePlan,
		activatePlan: handleActivatePlan,
		updateSlot: handleUpdateSlot,
		setSlotZones: handleSetSlotZones,
	};
};

export default useSortingMachine;
